import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import { Box, CircularProgress, IconButton } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import moment from "moment";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setState as setScenarioState } from "../../Setup/Scenario/scenarioSetupNewSlice";
import {
	getDataSources,
	getCustGrpRef,
	getScenario,
	getScenarioDetails as ScenarioDetails,
	getScDSAData,
	getUserCountriesEdit,
	setState as setEditScenarioState,
} from "../../Setup/Scenario/Edit/editScenarioNewSlice";
import {
	approveScenario,
	GetScenarioModificationDetails,
	getScenarioDetails,
	getScenarioHistory,
	getScenariosForApproval,
	setState,
} from "./scenarioApprovalSlice";
import { getScenarioHistoryRows } from "./scenarioApprovalUtils";

function getIsRestrictedAccess() {
	const data = JSON.parse(localStorage.getItem("data"));
	return data?.restrictedAccess == 0;
}

function refreshScenarios(dispatch, state) {
	const { selectedPeriod, selectedScenarioStatus, selectedCountryID, searchText } =
		state;
	dispatch(setState("scenarioDetails", null));
	dispatch(setState("rejectSelectedComment", ""));
	dispatch(setState("selectedRows", []));
	const logedUser = JSON.parse(localStorage.getItem("data"));
	dispatch(
		getScenariosForApproval({
			logedUser,
			period: selectedPeriod,
			scenarioStatus: selectedScenarioStatus,
			searchText,
			countryId: selectedCountryID,
		})
	);
}

function buildApprovePayload(scenarioId, scenariosForApproval) {
	const user = JSON.parse(localStorage.getItem("data"));
	const scenario = scenariosForApproval.find((s) => s.scenarioID === scenarioId);
	const country = user.userCountries.find(
		(index) => index.country.countryID === scenario.countryID
	).country;

	const formData = new FormData();
	formData.append("countryID", scenario.countryID);
	formData.append("countryName", country.countryName);
	formData.append("countryCode", country.countryCode);
	formData.append(
		"countrySalesOrganizationCode",
		country.countrySalesOrganizationCode
	);
	formData.append("scenarioID", scenario.scenarioID);
	formData.append("effectiveStartDate", scenario.effectiveStartDate);
	formData.append("effectiveEndDate", scenario.effectiveEndDate);
	formData.append("scenarioName", scenario.scenarioName);
	formData.append("scenarioStatus", scenario.scenarioStatus);
	formData.append("updatedBy", scenario.updatedBy);
	formData.append("updatedDate", scenario.updatedDate);
	formData.append("createdBy", scenario.createdBy);
	formData.append("createdDate", scenario.createdDate);
	formData.append("canApproveReject", true);
	formData.append("user[userID]", user.userID);
	formData.append("user[firstName]", user.firstName);
	formData.append("user[lastName]", user.lastName);
	formData.append("user[email]", user.email);
	formData.append("user[roleID]", user.roleID);
	formData.append("user[roleName]", user.roleName);
	formData.append("user[isAdmin]", user.isAdmin);
	formData.append("user[isAnalyst]", user.isAnalyst);
	formData.append("user[isApprover]", user.isApprover);
	formData.append("user[isFirstTimeUser]", user.isFirstTimeUser);
	formData.append("user[isHQUser]", user.isHQUser);
	formData.append("user[isInsert]", user.isInsert);
	formData.append("user[isLocalUser]", user.isLocalUser);
	formData.append("user[activeFlag]", user.activeFlag);
	formData.append("user[passwordChangeDate]", user.passwordChangeDate);
	formData.append("user[isIMSUser]", user.isIMSUser);

	user.userCountries.forEach((countryItem, index) => {
		formData.append(`user[userCountries][${index}][userID]`, countryItem.userID);
		formData.append(
			`user[userCountries][${index}][country][countryID]`,
			countryItem.country.countryID
		);
		formData.append(
			`user[userCountries][${index}][country][countryName]`,
			countryItem.country.countryName
		);
		formData.append(
			`user[userCountries][${index}][country][countryCode]`,
			countryItem.country.countryCode
		);
		formData.append(
			`user[userCountries][${index}][country][countrySalesOrganizationCode]`,
			countryItem.country.countrySalesOrganizationCode
		);
	});

	user.userCustomerTypes.forEach((type, index) => {
		formData.append(`user[userCustomerTypes][${index}][userID]`, type.userID);
		formData.append(
			`user[userCustomerTypes][${index}][customerTypeBO][customerTypeCode]`,
			type.customerTypeBO.customerTypeCode
		);
		formData.append(
			`user[userCustomerTypes][${index}][customerTypeBO][customerTypeName]`,
			type.customerTypeBO.customerTypeName
		);
	});
	formData.append("user[token]", user.token);

	return formData;
}

function HistoryRejectButton({ params, parentScenarioId }) {
	const dispatch = useDispatch();
	const isRestrictedAccess = getIsRestrictedAccess();

	function openRejectModal(e) {
		e.stopPropagation();
		dispatch(getScenarioDetails({ scenarioId: parentScenarioId })).then(() => {
			dispatch(setState("rejectScenarioModalOpen", true));
		});
	}

	if (params.row.canApproveReject && isRestrictedAccess) {
		return (
			<IconButton onClick={openRejectModal}>
				<CancelIcon sx={{ color: "red.main" }} />
			</IconButton>
		);
	}
	return null;
}

function HistoryApproveButton({ params, parentScenarioId }) {
	const dispatch = useDispatch();
	const state = useSelector((store) => store.scenarioApproval);
	const { scenariosForApproval } = state;
	const isRestrictedAccess = getIsRestrictedAccess();

	function approve(e) {
		e.stopPropagation();
		const formData = buildApprovePayload(parentScenarioId, scenariosForApproval);
		dispatch(approveScenario(formData)).then(() => {
			refreshScenarios(dispatch, state);
			dispatch(getScenarioHistory({ ScenarioID: parentScenarioId }));
		});
	}

	if (params.row.canApproveReject && isRestrictedAccess) {
		return (
			<IconButton onClick={approve}>
				<CheckCircleIcon sx={{ color: "green.main" }} />
			</IconButton>
		);
	}
	return null;
}

function HistoryEditButton({ params, parentScenarioId }) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { scenariosForApproval, countries } = useSelector(
		(store) => store.scenarioApproval
	);
	const isRestrictedAccess = getIsRestrictedAccess();

	function gotoEditScenario(e) {
		e.stopPropagation();
		const scenario = scenariosForApproval.find(
			(s) => s.scenarioID === parentScenarioId
		);
		if (!scenario) return;

		const { scenarioName, effectiveStartDate, effectiveEndDate } = scenario;
		const countryID = scenario.countryID;

		dispatch(setScenarioState("editMode", true));
		navigate("/setup/scenario");

		dispatch(getUserCountriesEdit()).then(() => {
			dispatch(setEditScenarioState("countryID", countryID));
			dispatch(
				setEditScenarioState(
					"selectedCountry",
					countries.find((country) => country.countryID === countryID).countryName
				)
			);
		});
		dispatch(getDataSources({ countryID }));
		dispatch(getCustGrpRef({ countryID }));
		dispatch(
			getScenario({
				countryId: countryID,
				scenario: scenarioName,
				fromDate: "",
				toDate: "",
			})
		).then(() => {
			dispatch(setEditScenarioState("editScID", parentScenarioId));
			dispatch(setEditScenarioState("editScName", scenarioName));
		});
		dispatch(setEditScenarioState("navigated_ScenarioName", scenarioName));
		dispatch(ScenarioDetails({ scenarioId: parentScenarioId }));
		dispatch(getScDSAData({ scenarioId: parentScenarioId }));
	}

	if (params.row.canEdit && params.row.isLatestSubmission && isRestrictedAccess) {
		return (
			<IconButton
				sx={{
					padding: 0,
					"&:hover": {
						backgroundColor: "#F8E9E7",
						borderRadius: "2px",
					},
				}}
			>
				<EditIcon
					onClick={gotoEditScenario}
					sx={{
						color: "#000000",
						fontSize: "large",
						borderRadius: "2px",
						width: 25,
						height: 22,
						backgroundColor: "transparent",
					}}
				/>
			</IconButton>
		);
	}
	return null;
}

function getScenarioHistoryColumns(parentScenarioId) {
	return [
		{
			field: "submissionNumber",
			headerName: "Submission No",
			flex: 1,
			minWidth: 130,
		},
		{
			field: "status",
			headerName: "Submission Status",
			flex: 1,
			minWidth: 140,
		},
		{
			field: "modifiedDate",
			headerName: "Last Action Date",
			flex: 1,
			minWidth: 130,
			valueFormatter: (params) =>
				params.value ? moment(params.value).format("DD/MM/YYYY") : "",
		},
		{
			field: "modifiedBy",
			headerName: "Last Action By",
			flex: 1,
			minWidth: 160,
		},
		{
			field: "reject",
			headerName: "Reject Scenario",
			flex: 1,
			minWidth: 150,
			align: "center",
			headerAlign: "center",
			sortable: false,
			filterable: false,
			renderCell: (params) => (
				<HistoryRejectButton params={params} parentScenarioId={parentScenarioId} />
			),
		},
		{
			field: "approve",
			headerName: "Approve Scenario",
			flex: 1,
			minWidth: 150,
			align: "center",
			headerAlign: "center",
			sortable: false,
			filterable: false,
			renderCell: (params) => (
				<HistoryApproveButton
					params={params}
					parentScenarioId={parentScenarioId}
				/>
			),
		},
		{
			field: "edit",
			headerName: "Edit",
			flex: 0.6,
			minWidth: 70,
			align: "center",
			headerAlign: "center",
			sortable: false,
			filterable: false,
			renderCell: (params) => (
				<HistoryEditButton params={params} parentScenarioId={parentScenarioId} />
			),
		},
	];
}

export default function ScenarioHistoryDetailPanel({
	row,
	layout = { left: 0, width: 0 },
}) {
	const dispatch = useDispatch();
	const parentScenarioId = row.id;
	const { ScenarioHistory, selectedHistoryScenarioID, historyLoading, selectedScenarioModificationID } =
		useSelector((store) => store.scenarioApproval);

	const columns = useMemo(
		() => getScenarioHistoryColumns(parentScenarioId),
		[parentScenarioId]
	);

	const handleHistoryRowClick = (params) => {
		const modificationId =
			params.row.scenarioModificationID ?? params.row.id ?? 0;
		const scenarioId = params.row.scenarioID ?? parentScenarioId;
		dispatch(
			GetScenarioModificationDetails({
				ScenarioID: scenarioId,
				ScenarioModificationID: modificationId,
			})
		);
	};

	if (row.id !== selectedHistoryScenarioID) {
		return null;
	}

	if (historyLoading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
				<CircularProgress size={24} />
			</Box>
		);
	}

	const historyRows = getScenarioHistoryRows(ScenarioHistory);

	return (
		<Box
			className="scenario-history-detail-panel"
			sx={{
				ml: `${layout.left}px`,
				width: layout.width > 0 ? `${layout.width}px` : "100%",
				maxWidth: layout.width > 0 ? `${layout.width}px` : "100%",
				boxSizing: "border-box",
			}}
		>
			<DataGridPro
				rows={historyRows}
				columns={columns}
				density="compact"
				hideFooter
				autoHeight
				disableColumnMenu
				disableRowSelectionOnClick
				onRowClick={handleHistoryRowClick}
				getRowClassName={(params) =>
					selectedScenarioModificationID != null &&
					(params.row.scenarioModificationID === selectedScenarioModificationID ||
						params.id === selectedScenarioModificationID)
						? "scenario-history-row-selected"
						: ""
				}
				onCellClick={(param, event) => {
					if (
						param.field === "reject" ||
						param.field === "approve" ||
						param.field === "edit"
					) {
						event.stopPropagation();
					}
				}}
				sx={{
					border: "1px solid",
					borderColor: "grey.300",
					"& .MuiDataGrid-columnHeaders": {
						backgroundColor: "grey.200",
					},
					"& .MuiDataGrid-cell": {
						whiteSpace: "normal !important",
						wordWrap: "break-word !important",
					},
				}}
			/>
		</Box>
	);
}
